import { useCallback, useContext, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { getSalesGroupModalConfigWithDeepLink } from 'src/modals/SalesGroup';
import {
  InventoryDeeplinkQueryParam,
  PurchaseDeeplinkQueryParam,
  SaleDeeplinkQueryParam,
  SalesGroupDeeplinkQueryParam,
} from 'src/utils/constants/constants';
import {
  getDeepLinkIdFromUrl,
  isInFullPageEventView,
} from 'src/utils/deepLinkUtils';
import { splitOutTicketGroupId } from 'src/utils/purchaseUtils';
import { Feature, ListingDetailDataField } from 'src/WebApiController';

import { useListingActions } from './useListingActions';
import { usePurchaseActions } from './usePurchaseActions';
import { useSaleActions } from './useSaleActions';
import { useUserHasFeature } from './useUserHasFeature';

export type DeeplinkType =
  | 'purchase'
  | 'sale'
  | 'salesGroup'
  | 'inventory'
  | null;

export interface MultipleDeeplinksResult {
  activeId: string;
  activeType: DeeplinkType;
  inventoryId: string;
  purchaseId: string;
  saleId: string;
  salesGroupId: string;
  openActiveModal: (inventoryCondition?: () => boolean) => void;
}

/**
 * Hook that extracts multiple deeplink IDs from the current URL
 * and provides a function to open the corresponding modal
 * Following existing codebase patterns for deeplink handling
 *
 *
 * @returns {MultipleDeeplinksResult} Object containing activeId, activeType, individual IDs, and modal opener
 */
export const useMultipleDeeplinks = (): MultipleDeeplinksResult => {
  const { openListingModal } = useListingActions();
  const { openSaleModal } = useSaleActions();
  const { openPurchaseModal } = usePurchaseActions();
  const { setModal } = useContext(ModalContext);
  const hasSalesGroupViewFeature = useUserHasFeature(Feature.SalesGroupView);

  const hasCreatePurchaseFromListingFeature = useUserHasFeature(
    Feature.CreatePurchaseFromListing
  );

  const { mainRoute } = useAppContext();
  const isEventPage = isInFullPageEventView(mainRoute);

  const inventoryId = getDeepLinkIdFromUrl(
    InventoryDeeplinkQueryParam,
    window.location.href
  );

  const purchaseIdRaw = getDeepLinkIdFromUrl(
    PurchaseDeeplinkQueryParam,
    window.location.href
  );

  // Extract just the purchase ID from combined format like "9484531||{"ticketGroupId":13930543}"
  const { id } = splitOutTicketGroupId(purchaseIdRaw);
  const purchaseId = String(id);

  const saleId = getDeepLinkIdFromUrl(
    SaleDeeplinkQueryParam,
    window.location.href
  );

  const salesGroupId = getDeepLinkIdFromUrl(
    SalesGroupDeeplinkQueryParam,
    window.location.href
  );

  const { activeId, activeType } = useMemo(() => {
    // Allow simple purchase deeplinks (creating new purchases, viewing existing purchases without ticket group editing)
    // on event pages. Block complex deeplinks with ticketGroup context on event pages due to side table;
    // integration issues where ticketGroupContext needs to be passed separately (see POS-6928).
    // On non-event pages, all purchase deeplinks work regardless of context.
    if (purchaseIdRaw && hasCreatePurchaseFromListingFeature) {
      const {
        id,
        ticketGroupId,
        ticketGroupEditReason,
        ticketGroupEditContext,
      } = splitOutTicketGroupId(purchaseIdRaw);

      // Check if there's any ticketGroup context beyond just the ID
      const hasContext =
        ticketGroupId != null ||
        ticketGroupEditReason != null ||
        Object.keys(ticketGroupEditContext || {}).filter(
          (k) => k !== 'ticketGroupId'
        ).length > 0;

      // Allow simple purchase deeplinks on event pages (e.g., creating new purchase with ID "0")
      if (!hasContext || !isEventPage) {
        return { activeId: String(id), activeType: 'purchase' as const };
      }
    }

    if (isEventPage) return { activeId: '', activeType: null };
    if (purchaseId && !hasCreatePurchaseFromListingFeature)
      return { activeId: purchaseId, activeType: 'purchase' as const };
    // POS-8918: salesGroup takes priority over sale — if both are present,
    // open the Sales Group modal (the group view subsumes the individual sale).
    if (salesGroupId && hasSalesGroupViewFeature)
      return {
        activeId: salesGroupId,
        activeType: 'salesGroup' as const,
      };
    if (saleId) return { activeId: saleId, activeType: 'sale' as const };
    if (inventoryId)
      return { activeId: inventoryId, activeType: 'inventory' as const };
    return { activeId: '', activeType: null };
  }, [
    purchaseIdRaw,
    hasCreatePurchaseFromListingFeature,
    isEventPage,
    purchaseId,
    salesGroupId,
    hasSalesGroupViewFeature,
    saleId,
    inventoryId,
  ]);

  const openActiveModal = useCallback(() => {
    if (!activeId?.length) return;

    switch (activeType) {
      case 'purchase':
        openPurchaseModal(activeId);
        break;
      case 'salesGroup':
        setModal(getSalesGroupModalConfigWithDeepLink(activeId));
        break;
      case 'sale':
        openSaleModal(activeId);
        break;
      case 'inventory':
        openListingModal(
          activeId,
          [ListingDetailDataField.Basic], // dataFields
          true, // forceRefresh
          false, // resetData
          true // sectional
        );
        break;
    }
  }, [
    activeId,
    activeType,
    openListingModal,
    openSaleModal,
    openPurchaseModal,
    setModal,
  ]);

  return {
    activeId, // Follows existing codebase naming convention
    activeType, // New: indicates which type of deeplink is active
    inventoryId, // Individual IDs available for specific use cases
    purchaseId,
    saleId,
    salesGroupId,
    openActiveModal, // Function to open the active modal
  };
};

export default useMultipleDeeplinks;
